import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Modal from '../../Modal';
import { SelectElement, OptionElement } from '../../SelectElement';
import { Button, ButtonStyles, ButtonSize } from '../../Button';
import { NoteFieldKind } from '../../../shared/db/note-extra-fields-db';
import styles from './new-note-field-modal.module.scss';

const FIELD_TYPE_OPTIONS: { value: NoteFieldKind; text: string }[] = [
  { value: 'select', text: 'Text with suggestion' },
  { value: 'text', text: 'Text' },
  { value: 'number', text: 'Number' },
  { value: 'boolean', text: 'Yes / No' },
  { value: 'textarea', text: 'Text area' },
  { value: 'date', text: 'Date' },
  { value: 'file', text: 'File' },
];

const schema = yup.object().shape({
  name: yup.string().trim().required('Field name is required'),
});

interface FormValues {
  name: string;
  kind: NoteFieldKind;
}

interface NewNoteFieldModalProps {
  onClose: () => void;
  onSubmit: (values: { name: string; kind: NoteFieldKind }) => Promise<void> | void;
  onDelete?: () => void;
  initialValues?: { name: string; kind: NoteFieldKind } | null;
}

const NewNoteFieldModal: React.FC<NewNoteFieldModalProps> = ({ onClose, onSubmit, onDelete, initialValues }) => {
  const isEditMode = !!initialValues;

  const { register, handleSubmit, errors, watch, setValue } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: initialValues?.name ?? '',
      kind: initialValues?.kind ?? 'select',
    },
  });

  const kind = watch('kind') || initialValues?.kind || 'select';

  useEffect(() => {
    register({ name: 'kind' });
  }, [register]);

  useEffect(() => {
    setValue('kind', initialValues?.kind ?? 'select');
  }, [initialValues, setValue]);

  const onFormSubmit = async (values: FormValues) => {
    const selectedKind = (values.kind || kind || initialValues?.kind || 'select') as NoteFieldKind;
    await onSubmit({ name: values.name.trim(), kind: selectedKind });
    onClose();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <Modal onBackdropClick={onClose} className={styles.modal}>
      <div className={styles.card}>
        <h3 className={styles.title}>{isEditMode ? 'Edit Field' : 'Create New Field'}</h3>
        <p className={styles.subtitle}>
          {isEditMode
            ? 'Update the label for this field.'
            : 'Add a custom label that will appear as a new row in this note.'}
        </p>

        <form onSubmit={handleSubmit(onFormSubmit)} className={styles.form}>
          <label className={styles.label}>
            Field name
            <input
              type="text"
              name="name"
              ref={register()}
              className={styles.input}
              maxLength={50}
              autoFocus
            />
          </label>
          {!!errors.name && <div className={styles.error}>{errors.name.message}</div>}

          <label className={styles.label}>
            Field type
            <div className={styles.selectWrapper}>
              <SelectElement
                value={kind}
                onChange={({ target: { value } }) => setValue('kind', value as NoteFieldKind)}
                Component={OptionElement}
                options={FIELD_TYPE_OPTIONS}
              />
            </div>
          </label>

          <div className={styles.actions}>
            <div style={{ flex: 1 }}>
              {isEditMode && onDelete && (
                <Button
                  type="button"
                  styleType={ButtonStyles.RED_WHITE}
                  size={ButtonSize.SM}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              )}
            </div>
            <Button type="button" styleType={ButtonStyles.WHITE_GRAY} size={ButtonSize.SM} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" styleType={ButtonStyles.ORANGE_WHITE} size={ButtonSize.SM}>
              {isEditMode ? 'Save' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default NewNoteFieldModal;